import { auth, db, database } from '../firebase';
import { ref, get, child } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';

// Esperar a que Firebase Auth resuelva su estado inicial antes de leer RTDB
const waitForAuthReady = () => new Promise((resolve) => {
  if (auth.currentUser) return resolve(auth.currentUser);
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    unsubscribe();
    resolve(user);
  });
});

// Configuración de Firebase
const functions = getFunctions();

// Modelos disponibles (se cargarán desde Firebase)
let AVAILABLE_MODELS = {};
let currentModelKey = null;

// Configuración de LM Studio (Local Inference Server)
const LM_STUDIO_MODEL_KEY = 'lm-studio-local';
const LM_STUDIO_DEFAULT_CONFIG = {
  apiKey: '',
  modelName: 'local-model',
  displayName: '🖥️ LM Studio (Local Server)',
  maxTokens: 16384,
  costPer1kTokens: 0.0,
  provider: 'lmstudio'
};

class VortexDocsService {
  constructor() {
    this.currentModel = null;
    this._initialized = false;
  }

  // Inicializar el servicio cargando modelos desde Firebase
  async initializeService() {
    if (this._initialized) return;
    try {
      await this.fetchOpenRouterModels();
    } catch (error) {
      console.error('Error inicializando VortexDocsService:', error);
    }
  }

  // Consultar modelos disponibles en LM Studio Local
  async fetchLocalLMStudioModels() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      const res = await fetch('http://127.0.0.1:1234/v1/models', { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) return {};
      const json = await res.json();
      const modelsList = json?.data || [];
      const localModels = {};

      modelsList.forEach((m) => {
        const id = m.id;
        if (!id || id.toLowerCase().includes('embedding')) return;

        let friendlyName = id;
        if (id.includes('qwen3.5-9b') || id.includes('qwen/qwen3.5-9b')) friendlyName = '🖥️ Qwen 3.5 9B (Local)';
        else if (id.includes('qwen2.5-coder-7b')) friendlyName = '🖥️ Qwen 2.5 Coder 7B (Local)';
        else if (id.includes('qwen2.5-vl-7b')) friendlyName = '🖥️ Qwen 2.5 VL 7B (Local)';
        else if (id.includes('deepseek-coder-6.7b-instruct')) friendlyName = '🖥️ DeepSeek Coder 6.7B (Local)';
        else if (id.includes('deepseek-coder-6.7b-kexer')) friendlyName = '🖥️ DeepSeek Coder Kexer (Local)';

//Qwen2.5 7B Instruct GGUF Q5_K_M
//Qwen2.5 7B Instruct Q4_K_M
        else friendlyName = `🖥️ Local: ${id}`;

        const key = `local_${id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
        localModels[key] = {
          apiKey: '',
          modelName: id,
          displayName: friendlyName,
          maxTokens: 16384,
          costPer1kTokens: 0.0,
          provider: 'lmstudio'
        };
      });

      return localModels;
    } catch (e) {
      return {};
    }
  }

  // Cargar modelos desde Firebase Realtime Database (igual que en Android)
  async fetchOpenRouterModels() {
    try {
      const localLMModels = await this.fetchLocalLMStudioModels();
      const user = await waitForAuthReady();

      // Sin sesión activa no se puede leer config/apis/modelos (regla exige auth != null).
      // Se usan los modelos por defecto y se reintentará al iniciar sesión.
      if (!user) {
        console.warn('⚠️ Sin sesión activa: se usan modelos por defecto. Se recargarán al iniciar sesión.');
        AVAILABLE_MODELS = {
          'gemini-2.5-flash-lite': {
            apiKey: 'AIzaSyDmSpKfIkntT8HY2KzgZoXM8UoNRsNARc4',
            modelName: 'gemini-2.5-flash-lite',
            displayName: 'Gemini 2.5 Flash Lite',
            maxTokens: 1000000,
            costPer1kTokens: 0.0001,
            provider: 'vertex-ai'
          },
          ...localLMModels
        };
        if (Object.keys(localLMModels).length === 0) {
          AVAILABLE_MODELS[LM_STUDIO_MODEL_KEY] = { ...LM_STUDIO_DEFAULT_CONFIG };
        }
        if (!currentModelKey) {
          currentModelKey = 'gemini-2.5-flash-lite';
          this.currentModel = currentModelKey;
        }
        return;
      }

      this._initialized = true;
      const modelosRef = ref(database, 'config/apis/modelos');
      const snapshot = await get(modelosRef);

      if (snapshot.exists()) {
        const models = {};
        snapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key;
          const modelData = childSnapshot.val();

          if (typeof modelData === 'object' && modelData !== null) {
            const rawModelName = modelData.modelName || modelData.displayName || key;
            // Determinar proveedor: respetar explícitamente el campo provider si viene en los datos
            const explicitProvider = modelData.provider || null;
            const isOpenRouter = explicitProvider === 'openrouter' ||
              (rawModelName && rawModelName.includes('/')) || // formato openrouter: "deepseek/deepseek-v4-flash"
              key.toLowerCase().includes('deepseek') ||
              key.toLowerCase().includes('openrouter');
            const isVertex = !isOpenRouter && (
              key.includes('vertex') ||
              key === 'gemini-2.5-flash-lite' ||
              explicitProvider === 'vertex-ai' ||
              (!explicitProvider && rawModelName && rawModelName.startsWith('gemini'))
            );
            const provider = isOpenRouter ? 'openrouter' : (isVertex ? 'vertex-ai' : (explicitProvider || 'openrouter'));

            models[key] = {
              apiKey: modelData.apiKey || modelData.gemini || '',
              modelName: rawModelName,
              displayName: modelData.displayName || key,
              maxTokens: modelData.maxTokens || (isVertex ? 1000000 : 65536),
              costPer1kTokens: modelData.costPer1kTokens || 0.003,
              provider
            };
          } else {
            // Soporte para formato de solo string (retrocompatibilidad)
            models[key] = {
              apiKey: '',
              modelName: modelData?.toString() || key,
              displayName: `${key} (${modelData})`,
              maxTokens: 4096,
              costPer1kTokens: 0.003
            };
          }
        });

        if (models['vertex_ai'] && !models['gemini-2.5-flash-lite']) {
          models['gemini-2.5-flash-lite'] = { ...models['vertex_ai'], displayName: 'Gemini 2.5 Flash Lite' };
        }
        if (models['gemini-2.5-flash-lite'] && !models['vertex_ai']) {
          models['vertex_ai'] = { ...models['gemini-2.5-flash-lite'], displayName: 'Gemini 2.5 Flash Lite' };
        }

        // Agregar modelos detectados en LM Studio
        if (Object.keys(localLMModels).length > 0) {
          Object.assign(models, localLMModels);
        } else {
          models[LM_STUDIO_MODEL_KEY] = { ...LM_STUDIO_DEFAULT_CONFIG };
        }

        AVAILABLE_MODELS = models;

        // Seleccionar modelo por defecto: priorizar DeepSeek (más barato), luego Gemini
        if (!currentModelKey || !models[currentModelKey]) {
          if (models['DeepSeek']) {
            currentModelKey = 'DeepSeek';
          } else if (models['gemini-2.5-flash-lite']) {
            currentModelKey = 'gemini-2.5-flash-lite';
          } else if (models['vertex_ai']) {
            currentModelKey = 'vertex_ai';
          } else {
            currentModelKey = Object.keys(models)[0];
          }
          this.currentModel = currentModelKey;
        }

        console.log('✅ Modelos de IA cargados desde Firebase y LM Studio:', Object.keys(models));
      } else {
        console.warn('⚠️ No se encontraron modelos en Firebase. Usando Gemini 2.5 Flash Lite por defecto.');
        AVAILABLE_MODELS = {
          'gemini-2.5-flash-lite': {
            apiKey: 'AIzaSyDmSpKfIkntT8HY2KzgZoXM8UoNRsNARc4',
            modelName: 'gemini-2.5-flash-lite',
            displayName: 'Gemini 2.5 Flash Lite',
            maxTokens: 1000000,
            costPer1kTokens: 0.0001,
            provider: 'vertex-ai'
          },
          ...localLMModels
        };
        if (Object.keys(localLMModels).length === 0) {
          AVAILABLE_MODELS[LM_STUDIO_MODEL_KEY] = { ...LM_STUDIO_DEFAULT_CONFIG };
        }
        currentModelKey = 'gemini-2.5-flash-lite';
        this.currentModel = currentModelKey;
      }
    } catch (error) {
      console.error('❌ Error cargando modelos desde Firebase:', error);
    }
  }

  // Extraer texto de un archivo
  async extractTextFromFile(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Para archivos de texto simples
      if (file.type === 'text/plain') {
        return await this.extractTextFromTextFile(file);
      }

      // Para archivos PDF
      if (file.type === 'application/pdf') {
        return await this.extractTextFromPDF(file);
      }

      // Para archivos DOCX
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return await this.extractTextFromDOCX(file);
      }

      // Para imágenes
      if (file.type.startsWith('image/')) {
        return await this.extractTextFromImage(file);
      }

      throw new Error('Tipo de archivo no soportado');
    } catch (error) {
      console.error('Error extracting text:', error);
      throw new Error(`Error al extraer texto: ${error.message}`);
    }
  }

  // Extraer texto de archivo de texto
  async extractTextFromTextFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      reader.onerror = (e) => {
        reject(new Error('Error al leer el archivo de texto'));
      };
      reader.readAsText(file);
    });
  }

  // Extraer texto de PDF usando PDF.js
  async extractTextFromPDF(file) {
    try {
      // Aquí necesitarías implementar la extracción de PDF
      // Por ahora, simulamos la extracción
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve('Texto extraído del PDF: ' + file.name + '\n\nEste es un texto de ejemplo extraído del documento PDF. En una implementación real, aquí aparecería el contenido real del PDF.');
        }, 2000);
      });
    } catch (error) {
      throw new Error('Error al extraer texto del PDF');
    }
  }

  // Extraer texto de DOCX
  async extractTextFromDOCX(file) {
    try {
      // Aquí necesitarías implementar la extracción de DOCX
      // Por ahora, simulamos la extracción
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve('Texto extraído del DOCX: ' + file.name + '\n\nEste es un texto de ejemplo extraído del documento DOCX. En una implementación real, aquí aparecería el contenido real del documento.');
        }, 2000);
      });
    } catch (error) {
      throw new Error('Error al extraer texto del DOCX');
    }
  }

  // Extraer texto de imagen usando OCR
  async extractTextFromImage(file) {
    try {
      // Aquí necesitarías implementar OCR
      // Por ahora, simulamos la extracción
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve('Texto extraído de la imagen: ' + file.name + '\n\nEste es un texto de ejemplo extraído de la imagen usando OCR. En una implementación real, aquí aparecería el texto real reconocido en la imagen.');
        }, 3000);
      });
    } catch (error) {
      throw new Error('Error al extraer texto de la imagen');
    }
  }

  // Generar resumen usando IA
  async generateSummary(text, userId) {
    try {
      const prompt = `Genera un resumen conciso y bien estructurado del siguiente texto. El resumen debe:
1. Capturar los puntos principales
2. Mantener la coherencia lógica
3. Ser fácil de entender
4. Tener entre 150-300 palabras

Texto a resumir:
${text}

Resumen:`;

      const response = await this.callOpenRouterAPI(prompt, userId, 'summary');
      return response;
    } catch (error) {
      console.error('Error generating summary:', error);
      throw new Error(`Error al generar resumen: ${error.message}`);
    }
  }

  // Generar cuestionario usando IA
  async generateQuiz(text, userId) {
    try {
      const prompt = `Genera un cuestionario de 10 preguntas basado en el siguiente texto. Las preguntas deben:
1. Ser de opción múltiple (A, B, C, D)
2. Cubrir los conceptos más importantes del texto
3. Tener una dificultad variada
4. Incluir la respuesta correcta marcada con un asterisco (*)

Formato requerido:
1. Pregunta aquí?
A) Opción A
B) Opción B
C) Opción C*
D) Opción D

Texto base:
${text}

Cuestionario:`;

      const response = await this.callOpenRouterAPI(prompt, userId, 'quiz');
      return response;
    } catch (error) {
      console.error('Error generating quiz:', error);
      throw new Error(`Error al generar cuestionario: ${error.message}`);
    }
  }

  // Retry con exponential backoff y fallback automático para 429 y cuota excedida
  async _retryWithBackoff(fn, maxRetries = 10) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Si a partir del intento 3 el modelo activo sufre 429, se intenta fallback automático a gemini-2.5-flash-lite
        const fallbackModelKey = (attempt >= 3) ? 'gemini-2.5-flash-lite' : null;
        return await fn(fallbackModelKey);
      } catch (error) {
        const errorMsg = String(error?.message || error || '');
        const status = error?.status || error?.statusCode;

        // Si es un error de conexión de LM Studio o modelo local, no reintentar en bucle
        if (errorMsg.includes('LM Studio') || errorMsg.includes('127.0.0.1:1234') || errorMsg.includes('localhost:1234')) {
          throw error;
        }

        const is429 = status === 429 ||
          errorMsg.includes('429') ||
          errorMsg.includes('Too Many Requests') ||
          errorMsg.includes('Resource exhausted') ||
          errorMsg.includes('quota') ||
          errorMsg.includes('Quota exceeded') ||
          errorMsg.includes('RATE_LIMIT_EXCEEDED');

        // Si el modelo fue retirado por Google Vertex AI (404 / RETIRED_MODEL), no reintentar
        const isRetired = errorMsg.includes('RETIRED_MODEL') || errorMsg.includes('retired') || status === 404;
        if (isRetired) {
          console.warn(`[VortexDocsService] ⚠️ El modelo fue retirado por Google Vertex AI (404 RETIRED_MODEL).`);
          throw error;
        }

        if (!is429 || attempt === maxRetries) throw error;

        const baseDelay = 4000;
        const jitter = Math.random() * 2000;
        const delay = Math.min(baseDelay * Math.pow(1.6, attempt - 1) + jitter, 45000);
        console.warn(`[VortexDocsService] ⏳ Rate limit / Cuota excedida (429) en intento ${attempt}/${maxRetries}. Reintentando en ${Math.round(delay)}ms...${attempt >= 2 ? ' (Próximo intento probará fallback a Flash Lite)' : ''}`);
        await new Promise(res => setTimeout(res, delay));
      }
    }
  }

  // Llamada directa a LM Studio Local Server (puerto 1234 por defecto)
  async callLMStudioDirect(prompt, systemInstruction = null, modelInfo = {}, maxTokens = 4096) {
    let modelName = modelInfo.modelName || 'local-model';
    console.log(`🖥️ LM Studio Local → Conectando a http://127.0.0.1:1234/v1 (${modelName})...`);

    // Si modelName es genérico o 'local-model', resolver automáticamente con el primer modelo disponible en LM Studio
    if (modelName === 'local-model' || !modelName) {
      try {
        const modelsRes = await fetch('http://127.0.0.1:1234/v1/models');
        if (modelsRes.ok) {
          const modelsData = await modelsRes.json();
          const validModels = (modelsData?.data || []).filter(m => !m.id?.toLowerCase().includes('embedding'));
          if (validModels.length > 0 && validModels[0]?.id) {
            modelName = validModels[0].id;
            console.log(`🖥️ LM Studio Local → Modelo autodetectado: ${modelName}`);
          }
        }
      } catch (checkErr) {
        // Continuar
      }
    }

    const messages = [];
    if (systemInstruction) {
      messages.push({ role: 'system', content: systemInstruction });
    }
    messages.push({ role: 'user', content: prompt });

    const body = {
      model: modelName,
      messages,
      max_tokens: maxTokens || 4096,
      temperature: 0.2,
    };

    let res;
    try {
      res = await fetch('http://127.0.0.1:1234/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
    } catch (netErr) {
      console.error('❌ Error de conexión con LM Studio:', netErr);
      throw new Error(
        'No se pudo conectar con LM Studio en http://127.0.0.1:1234. ' +
        'Asegúrate de: 1) Abrir LM Studio, 2) Iniciar el Local Server (puerto 1234), y 3) Habilitar la opción CORS en LM Studio.'
      );
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('❌ LM Studio error response detallado:', err);
      const msg = err.error?.message || err.message || `HTTP ${res.status}: ${res.statusText}`;
      if (msg.includes('No models loaded')) {
        throw new Error(
          'LM Studio tiene el servidor iniciado pero NO tiene ningún modelo cargado en memoria RAM/VRAM. ' +
          'Por favor ve a LM Studio, haz clic arriba en "Select a model to load" y carga tu modelo.'
        );
      }
      throw new Error(`LM Studio devolvió un error (${res.status}): ${msg}`);
    }

    const data = await res.json();
    const text = data.choices?.[0]?.message?.content?.trim();
    if (!text) throw new Error('Respuesta vacía recibida de LM Studio.');
    return text;
  }

  // Llamada directa a OpenRouter (DeepSeek, etc.) usando chat completions
  async callOpenRouterDirect(prompt, systemInstruction = null, modelInfo = {}, maxTokens = 16384) {
    const apiKey = modelInfo.apiKey || '';
    const modelName = modelInfo.modelName || 'deepseek/deepseek-v4-flash';

    if (!apiKey) throw new Error('No hay API Key de OpenRouter configurada.');

    console.log(`🚀 OpenRouter → modelo: ${modelName}`);

    const messages = [];
    if (systemInstruction) {
      messages.push({ role: 'system', content: systemInstruction });
    }
    messages.push({ role: 'user', content: prompt });

    const body = {
      model: modelName,
      messages,
      max_tokens: maxTokens || 16384,
      response_format: { type: 'json_object' },
      temperature: 0.2,
    };

    const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
        'HTTP-Referer': 'https://vortex-c9404.web.app',
        'X-Title': 'Vortex EXANI-II',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.error?.message || `HTTP ${res.status}: ${res.statusText}`;
      const isRetryable = res.status === 429 || res.status === 503 ||
        msg.includes('quota') || msg.includes('overloaded') || msg.includes('rate limit');
      if (isRetryable) {
        console.warn(`⚠️ OpenRouter ${res.status}: ${msg}`);
      }
      throw new Error(msg);
    }

    const data = await res.json();
    const text = data.choices?.[0]?.message?.content?.trim();
    if (!text) throw new Error('Respuesta vacía de OpenRouter');
    return text;
  }

  // Llamada directa a Gemini Developer API con fallback inteligente de modelos
  async callGeminiDirect(prompt, systemInstruction = null, targetModelKey = null, maxTokens = undefined) {
    const modelKey = targetModelKey || this.currentModel || currentModelKey || 'DeepSeek';
    const modelInfo = AVAILABLE_MODELS[modelKey] || AVAILABLE_MODELS['DeepSeek'] || AVAILABLE_MODELS['vertex_ai'] || AVAILABLE_MODELS['gemini-2.5-flash-lite'] || {};

    // Si el modelo es LM Studio Local, enrutar directamente a callLMStudioDirect
    if (modelInfo.provider === 'lmstudio' || modelKey === LM_STUDIO_MODEL_KEY) {
      return await this.callLMStudioDirect(
        prompt,
        systemInstruction,
        modelInfo,
        maxTokens || modelInfo.maxTokens || 16384
      );
    }

    // Si el modelo es de OpenRouter, enrutar a callOpenRouterDirect
    if (modelInfo.provider === 'openrouter' || modelKey === 'DeepSeek' || modelInfo.modelName?.includes('/')) {
      try {
        return await this.callOpenRouterDirect(
          prompt,
          systemInstruction,
          modelInfo,
          maxTokens || modelInfo.maxTokens || 16384
        );
      } catch (openRouterErr) {
        console.warn(`⚠️ OpenRouter falló (${openRouterErr.message}). Intentando fallback a Gemini...`);
        // Continuar hacia Gemini como fallback
      }
    }

    const geminiApiKey = modelInfo.apiKey?.startsWith('AIza')
      ? modelInfo.apiKey
      : (AVAILABLE_MODELS['vertex_ai']?.apiKey || AVAILABLE_MODELS['gemini-2.5-flash-lite']?.apiKey || 'AIzaSyDmSpKfIkntT8HY2KzgZoXM8UoNRsNARc4');

    // Lista de modelos Gemini como fallback
    const candidateModels = [
      'gemini-2.5-flash',
      'gemini-flash-latest',
      'gemini-2.5-pro',
      'gemini-2.5-flash-lite',
    ];

    const uniqueCandidates = [...new Set(candidateModels)];
    let lastError = null;

    for (let attempt = 1; attempt <= 2; attempt++) {
      for (const modelName of uniqueCandidates) {
        try {
          console.log(`⚡ Gemini Developer API → modelo: ${modelName} (intento ${attempt})`);

          const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${geminiApiKey}`;
          const generationConfig = {
            responseMimeType: 'application/json',
            maxOutputTokens: maxTokens || 8192,
            thinkingConfig: { thinkingBudget: 0 }
          };

          const body = {
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            generationConfig,
          };
          if (systemInstruction) {
            body.systemInstruction = { parts: [{ text: systemInstruction }] };
          }

          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            const msg = err.error?.message || `HTTP ${res.status}: ${res.statusText}`;
            const isRetryable = res.status === 429 || res.status === 503 || res.status === 404 ||
              msg.includes('Quota exceeded') || msg.includes('high demand') ||
              msg.includes('overloaded') || msg.includes('RESOURCE_EXHAUSTED');

            if (isRetryable) {
              console.warn(`⚠️ Modelo ${modelName} devolvió ${res.status} (${msg}). Probando modelo alternativo...`);
              lastError = new Error(msg);
              continue;
            }
            throw new Error(msg);
          }

          const data = await res.json();
          const text = (data.candidates?.[0]?.content?.parts || [])
            .map((p) => p.text || '')
            .join('')
            .trim();

          if (!text) throw new Error('Respuesta vacía de Gemini Developer API');
          return text;
        } catch (err) {
          lastError = err;
          console.warn(`⚠️ Error con modelo ${modelName}: ${err.message}`);
        }
      }

      if (attempt < 2) {
        console.log('⏳ Esperando 3 segundos antes de reintentar modelos Gemini...');
        await new Promise(r => setTimeout(r, 3000));
      }
    }

    throw lastError || new Error('No se pudo obtener respuesta de ningún modelo.');
  }

  // Llamada principal a la IA — enruta a OpenRouter o Gemini según el proveedor
  async callOpenRouterAPI(prompt, userId, operationType, systemInstruction = null, targetModelKey = null) {
    return this._retryWithBackoff(async (fallbackModelKey) => {
      // Prioridad: argumento > modelo activo > DeepSeek si disponible > gemini por defecto
      let modelKey = fallbackModelKey || targetModelKey || this.currentModel || currentModelKey;
      if (!modelKey) {
        modelKey = AVAILABLE_MODELS['DeepSeek'] ? 'DeepSeek' : 'gemini-2.5-flash';
      }
      return await this.callGeminiDirect(
        prompt,
        systemInstruction,
        modelKey,
        AVAILABLE_MODELS[modelKey]?.maxTokens || 16384
      );
    });
  }

  // --- Lógica de Procesamiento y Reparación de JSON ---

  cleanAndRepairJson(text) {
    if (!text) return "";
    let cleaned = text.trim();
    console.log(`[VortexDocsService] 🛠️ Limpiando JSON (${cleaned.length} chars)...`);

    // 1. Quitar bloques de código markdown primero para evitar interferencias
    cleaned = cleaned.replace(/```json/gi, "").replace(/```/g, "").trim();

    // 2. Extraer el bloque [...] o {...} principal
    const firstBracket = cleaned.indexOf('[');
    const firstBrace = cleaned.indexOf('{');

    let startIndex = -1;
    if (firstBracket !== -1 && firstBrace !== -1) startIndex = Math.min(firstBracket, firstBrace);
    else startIndex = firstBracket !== -1 ? firstBracket : firstBrace;

    if (startIndex !== -1) {
      cleaned = cleaned.substring(startIndex);
    }

    // 3. Eliminar caracteres de control no permitidos en JSON
    // eslint-disable-next-line no-control-regex
    cleaned = cleaned.replace(new RegExp('[\\u0000-\\u001F\\u007F-\\u009F]', 'g'), (match) => {
      if (match === '\n' || match === '\r' || match === '\t') return match;
      return ' ';
    });

    // 4. Analizar estado de strings y stack de llaves/corchetes LIFO
    const stack = [];
    let inString = false;
    let escaped = false;

    for (let i = 0; i < cleaned.length; i++) {
      const ch = cleaned[i];
      if (inString) {
        if (ch === '\\' && !escaped) {
          escaped = true;
        } else if (ch === '"' && !escaped) {
          inString = false;
        } else {
          escaped = false;
        }
      } else {
        if (ch === '"') {
          inString = true;
          escaped = false;
        } else if (ch === '{') {
          stack.push('}');
        } else if (ch === '[') {
          stack.push(']');
        } else if (ch === '}' || ch === ']') {
          if (stack.length > 0 && stack[stack.length - 1] === ch) {
            stack.pop();
          }
        }
      }
    }

    // Si quedó un string sin cerrar al final, cerrarlo
    if (inString) {
      cleaned += '"';
    }

    // Quitar comas colgantes antes de cierres o al final
    cleaned = cleaned.trim().replace(/,\s*$/, '');

    // Si terminó con dos puntos ":" (ej: "prop": ), completar con valor nulo
    if (cleaned.endsWith(':')) {
      cleaned += ' null';
    }

    // 5. Cerrar todas las estructuras abiertas en el orden exacto inverso (LIFO)
    if (stack.length > 0) {
      console.log(`[VortexDocsService] 🩹 Reparando cierres con stack LIFO: ${stack.slice().reverse().join('')}`);
      while (stack.length > 0) {
        cleaned = cleaned.replace(/,\s*$/, '');
        const closing = stack.pop();
        cleaned += closing;
      }
    }

    // Limpieza final de comas sobrantes antes de cierres
    cleaned = cleaned.replace(/,\s*([\]}])/g, '$1');

    return cleaned;
  }

  safeJsonParse(str) {
    if (!str) return null;
    try {
      const cleaned = this.cleanAndRepairJson(str);
      try {
        const result = JSON.parse(cleaned);
        console.log(`[VortexDocsService] ✅ JSON parseado con éxito (Estándar).`);
        return result;
      } catch (e) {
        console.warn(`[VortexDocsService] ⚠️ Falló JSON.parse estándar (${e.message}). Iniciando extracción por bloques...`);

        // Intento de extracción por bloques de objetos {...}
        const rawText = cleaned;
        const objects = [];
        let currentOpenBraces = 0;
        let currentStartIndex = -1;
        let inStr = false;
        let esc = false;

        for (let i = 0; i < rawText.length; i++) {
          const ch = rawText[i];
          if (inStr) {
            if (ch === '\\' && !esc) esc = true;
            else if (ch === '"' && !esc) inStr = false;
            else esc = false;
            continue;
          }

          if (ch === '"') {
            inStr = true;
            esc = false;
          } else if (ch === '{') {
            if (currentOpenBraces === 0) currentStartIndex = i;
            currentOpenBraces++;
          } else if (ch === '}') {
            currentOpenBraces--;
            if (currentOpenBraces === 0 && currentStartIndex !== -1) {
              let objStr = rawText.substring(currentStartIndex, i + 1);
              objStr = objStr.replace(/,\s*([\]}])/g, '$1');
              try {
                const obj = JSON.parse(objStr);
                objects.push(obj);
              } catch {
                // Objeto ignorado si tiene sintaxis rota
              }
              currentStartIndex = -1;
            }
          }
        }

        if (objects.length > 0) {
          console.log(`[VortexDocsService] 🧩 Se rescataron ${objects.length} objetos mediante extracción.`);
          // Si el primer objeto parece ser el contenedor general o un array de objetos
          if (objects.length === 1 && (objects[0].lessons || objects[0].lecciones || objects[0].modules)) {
            return objects[0];
          }
          return objects;
        }
        throw e;
      }
    } catch (err) {
      console.error('[VortexDocsService] ❌ Error crítico parseando JSON:', err.message);
      return null;
    }
  }

  validateQuestions(questions, difficulty) {
    if (!Array.isArray(questions)) return [];

    return questions.map(q => {
      // Normalizar campos (la IA a veces usa nombres distintos)
      const questionText = q.question || q.pregunta || q.text || '';
      const options = q.options || q.respuestas || q.opciones || [];
      let explanation = q.explanation || q.explicacion || '';

      // Intentar encontrar el índice de la respuesta correcta
      let correctIdx = -1;
      const possibleCorrectValue = q.correctAnswer !== undefined ? q.correctAnswer :
        (q.respuesta_correcta !== undefined ? q.respuesta_correcta :
          (q.correct_index !== undefined ? q.correct_index : -1));

      if (typeof possibleCorrectValue === 'number') {
        correctIdx = possibleCorrectValue;
      } else if (typeof possibleCorrectValue === 'string') {
        // Si es un número en string "1"
        const parsed = parseInt(possibleCorrectValue);
        if (!isNaN(parsed)) {
          correctIdx = parsed;
        } else {
          // Si es el texto de la opción, buscar su índice
          correctIdx = options.findIndex(opt =>
            opt === possibleCorrectValue ||
            (typeof opt === 'object' && opt.respuesta === possibleCorrectValue)
          );
        }
      }

      // Normalizar opciones si vienen como objetos
      const normalizedOptions = options.map(opt => {
        if (typeof opt === 'object' && opt !== null) {
          return opt.respuesta || opt.texto || JSON.stringify(opt);
        }
        return String(opt);
      });

      return {
        id: q.id || Math.floor(Math.random() * 1000000),
        question: questionText,
        options: normalizedOptions,
        correctAnswer: correctIdx,
        explanation: explanation,
        difficulty: q.difficulty || difficulty
      };
    }).filter(q =>
      q.question && q.question.length > 5 &&
      Array.isArray(q.options) &&
      q.options.length === 4 &&
      q.options.every(opt => opt && opt.trim() !== '') &&
      q.correctAnswer >= 0 &&
      q.correctAnswer <= 3
    );
  }

  /**
   * Generar preguntas utilizando Vertex AI o OpenRouter
   * @param {string} text - Texto fuente del chunk o sección
   * @param {number} count - Número de preguntas a generar
   * @param {string} difficulty - Dificultad (easy/medium/hard)
   * @param {string} modelKey - Clave del modelo a usar
   * @param {string} userId - UID del usuario
   * @param {string[]} previousQuestions - Preguntas ya generadas (para evitar duplicados)
   * @param {string|null} institutionPrompt - Prompt base de la institución (INSTITUTION_PROMPTS[inst])
   * @param {object} extraOptions - Opciones avanzadas (ej. fullDocumentContext para Context Caching)
   */
  async generateQuestions(text, count, difficulty, modelKey, userId, previousQuestions = [], institutionPrompt = null, extraOptions = {}) {
    const activeKey = modelKey || this.currentModel || currentModelKey;
    const modelInfo = AVAILABLE_MODELS[activeKey] || { modelName: activeKey };
    const isLocal = modelInfo.provider === 'lmstudio' || activeKey.startsWith('local_');

    let systemPrompt, userPrompt;

    if (isLocal) {
      // --- Prompt ultra-compacto para modelos locales pequeños ---
      // Los modelos 7B/9B ignoran instrucciones complejas. Prompt minimalista + ejemplo en JSON.
      const textSample = text.substring(0, 2500);
      const prevWarning = previousQuestions.length > 0
        ? `No repitas estos temas: ${previousQuestions.slice(-8).join(', ')}. `
        : '';

      systemPrompt = 'Eres un asistente que genera preguntas de opción múltiple en formato JSON. Responde SOLO con el array JSON, sin texto adicional.';

      userPrompt = `Texto de referencia:
---
${textSample}
---
${prevWarning}Basándote únicamente en ese texto, genera EXACTAMENTE ${count} pregunta(s) de opción múltiple en español.

Responde SOLO con JSON válido, en este formato exacto (sin texto antes ni después):
[{"id":1,"question":"¿Pregunta?","options":["A","B","C","D"],"correctAnswer":0,"explanation":"Razón.","difficulty":"${difficulty}"}]

IMPORTANTE: El array debe tener exactamente ${count} elemento(s). SOLO JSON.`;

    } else {
      // --- Prompt completo para modelos cloud (Gemini, DeepSeek, OpenRouter) ---
      const baseInstPrompt = institutionPrompt ||
        `Eres un experto profesor naval y marítimo de la Armada de México. 
    Tu tarea es generar cuestionarios de alta calidad basados en textos proporcionados.`;

      const documentContextBlock = extraOptions?.fullDocumentContext
        ? `\n\nCONTEXTO DE REFERENCIA (fragmento ${extraOptions.sectionTarget || ''}):\n${extraOptions.fullDocumentContext.substring(0, 8000)}\n`
        : '';

      systemPrompt = `${baseInstPrompt}${documentContextBlock}
    
    REGLAS DE ORO:
    1. Responde ÚNICAMENTE con un ARRAY JSON de objetos. 
    2. Debes generar EXACTAMENTE la cantidad de preguntas que se te soliciten (${count}).
    3. NO te detengas hasta completar el número solicitado.
    4. Formato de cada objeto:
       {
         "id": 1, 
         "question": "...",
         "options": ["...", "...", "...", "..."],
         "correctAnswer": 0,
         "explanation": "...",
         "difficulty": "${difficulty}"
       }
    
    REGLAS ESTRICTAS PARA EL JSON:
    - Responde SOLO con el array JSON [...]. No incluyas explicaciones antes ni después.
    - NO incluyas comas (,) al final del último elemento de un array o de la última propiedad de un objeto (evita trailing commas).
    - El campo "options" debe ser un array de exactamente 4 strings reales extraídos del texto.
    - El campo "correctAnswer" debe ser el índice numérico (0-3) de la respuesta correcta.
    - No uses claves de nivel superior (como {"questions": [...]}). Solo el array plano.
    - Todo el contenido debe estar en Español.
    - Es CRÍTICO que el array JSON contenga EXACTAMENTE ${count} objetos de pregunta.`;

      userPrompt = extraOptions?.fullDocumentContext
        ? `INSTRUCCIÓN: Basándote en el documento de referencia cargado en contexto, concéntrate en la sección "${extraOptions.sectionTarget || 'Fragmento actual'}" (resumen: ${text.substring(0, 300)}...) y genera EXACTAMENTE ${count} preguntas únicas sobre ella. 
    
    ${previousQuestions.length > 0 ? `IMPORTANTE: NO repitas ni preguntes sobre los mismos temas que estas preguntas ya generadas:\n- ${previousQuestions.slice(-50).join('\n- ')}` : ''}`
        : `INSTRUCCIÓN: Analiza el texto proporcionado y genera EXACTAMENTE ${count} preguntas únicas sobre él. 
    
    Texto fuente:
    ${text}
    
    ${previousQuestions.length > 0 ? `IMPORTANTE: NO repitas ni preguntes sobre los mismos temas que estas preguntas ya generadas:\n- ${previousQuestions.slice(-50).join('\n- ')}` : ''} `;
    }

    try {
      let content = await this.callOpenRouterAPI(userPrompt, userId, 'quiz', systemPrompt, activeKey);

      if (!content) return [];

      // Para modelos locales: extracción agresiva del primer array JSON en el texto
      if (isLocal) {
        const arrayMatch = content.match(/\[[\s\S]*\]/);
        if (arrayMatch) {
          content = arrayMatch[0];
          console.log(`[VortexDocsService] ✂️ Array JSON extraído (${content.length} chars)`);
        } else {
          console.warn('[VortexDocsService] ⚠️ Respuesta del modelo local sin array JSON. Primeros 300 chars:', content.substring(0, 300));
          return [];
        }
      }

      const parsed = this.safeJsonParse(content);
      if (!parsed) return [];

      let questionsList = [];
      if (Array.isArray(parsed)) {
        questionsList = parsed.filter(o => o.question);
      } else if (parsed && typeof parsed === 'object') {
        questionsList = parsed.questions || parsed.cuestionario || (parsed.question ? [parsed] : []);
      }

      return this.validateQuestions(questionsList, difficulty);

    } catch (error) {
      console.error('[VortexDocsService] Error en generateQuestions:', error);
      throw error;
    }
  }

  // --- Fin Lógica de Procesamiento ---

  // Registrar uso de tokens
  async logTokenUsage(usage, operationType, userId) {
    try {
      // Aquí podrías guardar el uso de tokens en Firestore
      console.log('Token usage:', {
        userId,
        operationType,
        promptTokens: usage.prompt_tokens,
        completionTokens: usage.completion_tokens,
        totalTokens: usage.total_tokens,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error logging token usage:', error);
    }
  }

  // Obtener modelos disponibles
  getAvailableModels() {
    return AVAILABLE_MODELS;
  }

  // Cambiar modelo
  setModel(modelKey) {
    if (AVAILABLE_MODELS[modelKey] || modelKey === 'gemini-2.5-flash-lite' || modelKey === LM_STUDIO_MODEL_KEY) {
      this.currentModel = modelKey;
      currentModelKey = modelKey;
      return true;
    }
    return false;
  }

  // Obtener modelo actual
  getCurrentModel() {
    const modelKey = this.currentModel || currentModelKey;
    if (!modelKey) {
      return {
        key: 'gemini-2.5-flash-lite',
        modelName: 'gemini-2.5-flash-lite',
        displayName: 'Gemini 2.5 Flash Lite (Vertex AI)',
        maxTokens: 1000000,
        provider: 'vertex-ai'
      };
    }
    if (modelKey === LM_STUDIO_MODEL_KEY || AVAILABLE_MODELS[modelKey]?.provider === 'lmstudio') {
      return AVAILABLE_MODELS[modelKey] || { key: LM_STUDIO_MODEL_KEY, ...LM_STUDIO_DEFAULT_CONFIG };
    }
    const knownVertexModels = { 'gemini-2.5-flash-lite': 'Gemini 2.5 Flash Lite (Vertex AI)' };
    if (knownVertexModels[modelKey]) {
      return {
        key: modelKey,
        modelName: 'gemini-2.5-flash-lite',
        displayName: knownVertexModels[modelKey],
        maxTokens: 1000000,
        provider: 'vertex-ai'
      };
    }
    // Solo permitir gemini-2.5-flash-lite como modelo Vertex AI
    if (modelKey === 'gemini-2.5-flash-lite') {
      return {
        key: modelKey,
        modelName: 'gemini-2.5-flash-lite',
        displayName: 'Gemini 2.5 Flash Lite (Vertex AI)',
        maxTokens: 1000000,
        provider: 'vertex-ai'
      };
    }
    if (AVAILABLE_MODELS[modelKey]) {
      return { key: modelKey, ...AVAILABLE_MODELS[modelKey] };
    }
    // Fallback
    return {
      key: 'gemini-2.5-flash-lite',
      modelName: 'gemini-2.5-flash-lite',
      displayName: 'Gemini 2.5 Flash Lite (Vertex AI)',
      maxTokens: 1000000,
      provider: 'vertex-ai'
    };
  }

  // Recargar modelos desde Firebase
  async reloadModels() {
    await this.fetchOpenRouterModels();
  }

  // Calcular costo estimado
  calculateEstimatedCost(promptTokens, completionTokens = 0) {
    const modelKey = this.currentModel || currentModelKey;
    const model = AVAILABLE_MODELS[modelKey];
    if (!model) return 0;

    const totalTokens = promptTokens + completionTokens;
    return (totalTokens / 1000) * model.costPer1kTokens || 0;
  }
}

// Instancia singleton
const vortexDocsService = new VortexDocsService();

export default vortexDocsService;